// What a browser says when something broke in it, and what the admin reads of
// it (scenario O7, docs/design/PLAN-2026-10-09-error-reports.md).
//
// No server log sees a screen crash on a phone: the meter sees every request
// the API answers, and nothing at all of a render that threw. So the page
// reports it — `POST /api/client-errors`, open to anyone, signed in or not —
// and the admin tool's Errors tab reads the reports grouped by what failed and
// where (`GET /api/admin/errors`), and one group's reports when opened.
//
// THE LIMITS ARE HERE so the page clips to the same numbers the endpoint
// refuses past: each field is cut to fit, and then the stack further until the
// whole body is under the API's cap on a request body (8 KiB, `envelope.ts`).
// A report that arrives longer was not sent by our page.

import { z } from 'zod';
import { DeviceForm } from './activity';
import { pageOf } from './envelope';

/** The longest of each field a report may carry, and of the body as sent, in bytes. */
export const CLIENT_ERROR_LIMITS = {
  message: 500,
  stack: 6000,
  path: 300,
  api: 300,
  build: 64,
  setting: 32,
  /** Under the API's 8,192-byte cap on any request body, with room to spare. */
  body: 7800,
} as const;

/**
 * What broke.
 *
 * - `error`: an exception nothing caught (`window.onerror`).
 * - `rejection`: a promise that failed with nothing waiting on it.
 * - `crash`: a screen that threw while drawing, caught by the app's boundary,
 *   which showed the person a way on instead of a blank page.
 * - `drift`: the API answered in a shape this build does not read — the
 *   person saw "the site answered in a shape this page does not understand",
 *   or "this page is out of date" for a value newer than the build.
 */
export const ClientErrorKind = z.enum(['error', 'rejection', 'crash', 'drift']);
export type ClientErrorKind = z.infer<typeof ClientErrorKind>;

/** The layout the page was drawing, by width (`tierFor`, APP_SHELL.md). */
export const LayoutTier = z.enum(['phone', 'tablet', 'desktop', 'large']);
export type LayoutTier = z.infer<typeof LayoutTier>;

/** `POST /api/client-errors` — one thing that broke in a browser. */
export const ClientErrorReport = z.object({
  kind: ClientErrorKind,
  message: z.string().min(1).max(CLIENT_ERROR_LIMITS.message),
  stack: z.string().max(CLIENT_ERROR_LIMITS.stack).nullable().optional(),
  /** Where the page was: the path alone, never the query string. */
  path: z.string().min(1).max(CLIENT_ERROR_LIMITS.path),
  /** For `drift`, the request that answered: `GET /api/leagues/…`. */
  api: z.string().max(CLIENT_ERROR_LIMITS.api).nullable().optional(),
  /** The build's commit, `unknown` for one built without it. */
  build: z.string().min(1).max(CLIENT_ERROR_LIMITS.build),
  tier: LayoutTier.nullable().optional(),
  /** True in the installed app's own window (INSTALL.md). */
  installed: z.boolean().optional(),
  /** The look and the detail level the page was drawn in. */
  look: z.string().max(CLIENT_ERROR_LIMITS.setting).nullable().optional(),
  level: z.string().max(CLIENT_ERROR_LIMITS.setting).nullable().optional(),
});
export type ClientErrorReport = z.infer<typeof ClientErrorReport>;

/**
 * One group: the same kind of failure, the same message once its numbers and
 * ids are set aside, on the same screen.
 */
export const ClientErrorGroup = z.object({
  /** The group's key, for its reports: `GET /api/admin/errors/reports?group=`. */
  group: z.string(),
  kind: ClientErrorKind,
  /** The newest report's message, as it arrived. */
  message: z.string(),
  /** The screen's route (`/l/:leagueId/standings`); null for a path no route matches. */
  screen: z.string().nullable(),
  /** Reports in the window. */
  count: z.number().int().min(1),
  /** Distinct accounts among them; a signed-out visitor is not counted here. */
  people: z.number().int().min(0),
  /** Reports from nobody signed in. */
  signedOut: z.number().int().min(0),
  firstAt: z.string(),
  lastAt: z.string(),
  /** Reports by device form, as the browser's agent says it. */
  forms: z.object({
    phone: z.number().int().min(0),
    tablet: z.number().int().min(0),
    computer: z.number().int().min(0),
  }),
  /** The browsers among them, most reports first: "Safari on iPhone". */
  devices: z.array(z.string()),
  /** The builds that reported it, newest report first. */
  builds: z.array(z.string()),
});
export type ClientErrorGroup = z.infer<typeof ClientErrorGroup>;

/** `GET /api/admin/errors` — what broke in browsers, by group. */
export const ClientErrorsView = z.object({
  at: z.string(),
  /** How many days back it looks. */
  days: z.number().int().positive(),
  /** Reports in the window, all groups. */
  total: z.number().int().min(0),
  /** Most recently seen first, at most `CLIENT_ERROR_GROUPS_SHOWN`. */
  groups: z.array(ClientErrorGroup),
  /** True when there were more groups than were listed. */
  truncated: z.boolean(),
});
export type ClientErrorsView = z.infer<typeof ClientErrorsView>;

/** The most groups one read lists. */
export const CLIENT_ERROR_GROUPS_SHOWN = 100;

/** One report, as the admin reads it. */
export const ClientErrorEvent = z.object({
  id: z.string(),
  at: z.string(),
  kind: ClientErrorKind,
  message: z.string(),
  stack: z.string().nullable(),
  path: z.string(),
  screen: z.string().nullable(),
  /** The league the screen was in, when it was one. */
  leagueId: z.string().nullable(),
  api: z.string().nullable(),
  build: z.string(),
  tier: LayoutTier.nullable(),
  installed: z.boolean(),
  look: z.string().nullable(),
  level: z.string().nullable(),
  /** From the browser's agent: "Chrome on Android". */
  device: z.string().nullable(),
  form: DeviceForm.nullable(),
  /** Who was signed in; null for a visitor. */
  userId: z.string().nullable(),
  displayName: z.string().nullable(),
});
export type ClientErrorEvent = z.infer<typeof ClientErrorEvent>;

/** `GET /api/admin/errors/reports?group=` — one group's reports, newest first. */
export const ClientErrorEventsView = pageOf(ClientErrorEvent);
export type ClientErrorEventsView = z.infer<typeof ClientErrorEventsView>;
